import type { Dispatch, SetStateAction } from 'react';
import type AdyenCheckoutError from '@adyen/adyen-web/dist/types/core/Errors/AdyenCheckoutError';
import type { PaymentContextType } from '../../../context/paymentContext';
import { IS_GC_PAYMENT_METHOD, createAnchorTagHref, getItem } from '../../../utils';
import { isPayPalPayment } from '../../../utils/adyen';
import type { PaymentMethodTypes } from '../../../utils/adyen/adyen';
import { newRelicAvailabilityChecker } from '../../../utils/newrelic';

interface HandleErrorProps {
  error: AdyenCheckoutError;
  cartId: string;
  locale: string;
  paymentData: PaymentContextType['paymentData'];
  customerPaymentMethod: string | null;
  isGiftCardEnough: boolean;
  isGiftCardRemoved: boolean;
  setIsIframeError: Dispatch<SetStateAction<boolean>>;
  setIsErrorGiftCardInput: Dispatch<SetStateAction<boolean>>;
  setOnErrorGiftCard: Dispatch<SetStateAction<boolean>>;
}

export const ERROR_TYPES = {
  IFRAME_ERROR: 'secured field iframes',
  CANCEL: 'CANCEL',
} as const;

export const handleError = ({
  error,
  cartId,
  locale,
  paymentData,
  customerPaymentMethod,
  isGiftCardEnough,
  isGiftCardRemoved,
  setIsIframeError,
  setIsErrorGiftCardInput,
  setOnErrorGiftCard,
}: HandleErrorProps) => {
  newRelicAvailabilityChecker(() => {
    window.newrelic.addPageAction('checkout_payment_error', {
      cart_id: cartId,
      // error.name
      // NETWORK_ERROR - network error
      // CANCEL - shopper canceled the current transaction
      // IMPLEMENTATION_ERROR - the method or parameter are incorrect or are not supported
      // ERROR - generic error
      payment_error_name: error?.name ?? '',
      payment_error_message: error?.message ?? '',
      payment_error_cause: error?.cause ?? '',
      payment_session_id: paymentData?.session.id,
    });
  });

  const isFromGiftCard = !!getItem(IS_GC_PAYMENT_METHOD, true, 'sessionStorage');

  //   When user is on payment page, after a couple of seconds 'iframe failed to load' error occur
  //   It is internal adyen thing, which does not break payment in any way
  //   The only thing that helps with that is to remount adyen component in AdyenWebComponent.tsx
  //   This internal error seems to be fixed after bumping @adyen/adyen-web from 5.43.0 to 5.63.0 */
  if (error.message.toLowerCase().includes(ERROR_TYPES.IFRAME_ERROR)) {
    setIsIframeError(true);
  } else if (
    error.name !== ERROR_TYPES.CANCEL &&
    !isPayPalPayment(customerPaymentMethod as PaymentMethodTypes) &&
    !isFromGiftCard
  ) {
    window.location.href = createAnchorTagHref({ locale, path: '/checkout/payment/error' });
  }

  // For some reason, when `Add another gift card` is clicked
  // and an error occurs (wrong PIN, no balance, etc) it goes here (generic Adyen onError)
  // instead of GiftCard onError and customerPaymentMethod gets overwritten as well.
  // As workaround, added a session storage variable to indicate that user clicked on
  // `Add another gift card` so that when an error occurs, it does not get redirected to
  // /checkout/payment/error
  if (isFromGiftCard && !error.message.toLowerCase().includes(ERROR_TYPES.IFRAME_ERROR)) {
    setOnErrorGiftCard(true);

    if (!isGiftCardEnough || !isGiftCardRemoved) setIsErrorGiftCardInput(true);
  }
};
